import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { comics, categories } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find(c => c.slug === slug) || categories[0];

  const filteredComics = useMemo(
    () => comics.filter(c => c.category === currentCategory.slug),
    [currentCategory.slug]
  );

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name}漫画 - 布丁漫画`,
    description: `布丁漫画${currentCategory.name}分类，精选优质韩国${currentCategory.name}漫画，免费在线阅读。`,
    url: `https://puddingcomic.com/category/${currentCategory.slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredComics.map((comic, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Book',
          name: comic.title,
          author: { '@type': 'Person', name: comic.author },
          url: `https://puddingcomic.com/article/${comic.slug}`,
        },
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${currentCategory.name}漫画`}
        description={`布丁漫画${currentCategory.name}分类，海量正版韩国${currentCategory.name}漫画免费在线阅读。精选优质作品，持续更新。`}
        keywords={`布丁漫画,韩国漫画,${currentCategory.name}漫画,免费韩漫,正版韩漫在线阅读`}
        canonicalUrl={`https://puddingcomic.com/category/${currentCategory.slug}`}
        schema={categorySchema}
      />
      <Navbar />

      <main className="pt-16 bg-background-50">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '漫画分类', to: '/category/romance' },
            { label: `${currentCategory.name}漫画` },
          ]}
        />

        <section className="w-full px-4 md:px-6 lg:px-10 py-6">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
            {currentCategory.name}漫画
          </h1>

          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  cat.slug === currentCategory.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 border border-background-200 hover:bg-primary-100 hover:text-primary-600'
                }`}
              >
                <i className={`${cat.icon} text-xs`} />
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {filteredComics.map((comic) => (
                <Link
                  key={comic.slug}
                  to={`/article/${comic.slug}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
                    <img
                      src={comic.coverImage}
                      alt={`${comic.title} - 韩国${comic.category}漫画`}
                      title={`${comic.title} - 免费韩国漫画在线阅读`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute top-2 right-2 bg-primary-500 text-background-50 text-xs font-medium px-2 py-0.5 rounded-full">
                      {comic.rating}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                      <span className="text-xs text-background-50/90">{comic.status}</span>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">{comic.title}</h3>
                  <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {comic.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-1.5 py-0.5 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-book-2-line text-2xl" />
              </div>
              <p className="text-foreground-500">该分类暂无漫画，敬请期待</p>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}